import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "axios";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
).replace(/\/$/, "");

type SectionPayload = {
  client_id: number;
  user_id?: number;
};

const buildQuery = (
  user_id?: number
) => {
  return user_id
    ? `?user_id=${encodeURIComponent(
        String(user_id)
      )}`
    : "";
};

// ==========================
// ABOUT
// ==========================
export const fetchClubAboutSection =
  createAsyncThunk(
    "clubSection/fetchClubAboutSection",
    async (
      {
        client_id,
        user_id,
      }: SectionPayload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/client/about/${client_id}${buildQuery(
              user_id
            )}`
          );

        return res.data?.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data ||
            "Failed to fetch about"
        );
      }
    }
  );

// ==========================
// CLUB MEDIA
// ==========================
// ==========================
// CLUB MEDIA
// ==========================
export const fetchClubMediaSection =
  createAsyncThunk(
    "clubSection/fetchClubMediaSection",
    async (
      {
        client_id,
        user_id,
      }: SectionPayload,
      { rejectWithValue }
    ) => {
      try {
        const formData = new FormData();

        formData.append(
          "client_id",
          String(client_id)
        );

        if (
          user_id &&
          Number(user_id) > 0
        ) {
          formData.append(
            "user_id",
            String(user_id)
          );
        }

        const res = await axios.post(
          `${API_BASE}/media-albums/get-visible-albums`,
          formData
        );

        return res.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data ||
            "Failed to fetch club media"
        );
      }
    }
  );

export const fetchClubMembersSection =
  createAsyncThunk(
    "clubSection/fetchClubMembersSection",
    async (
      {
        client_id,
        user_id,
      }: SectionPayload,
      { rejectWithValue }
    ) => {
      try {
        const res = await axios.get(
          `${API_BASE}/client/members/${client_id}${buildQuery(
            user_id
          )}`
        );

        return res.data?.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data ||
            "Failed to fetch members"
        );
      }
    }
  );

// ==========================
// PROGRAMS / EVENTS SECTION
// Backend response:
// {
//   data: {
//     can_view_events,
//     events_visibility,
//     is_linked_member,
//     private_message,
//     total_events,
//     events: []
//   }
// }
// ==========================
export const fetchClubEventsSection =
  createAsyncThunk(
    "clubSection/fetchClubEventsSection",
    async (
      {
        client_id,
        user_id,
      }: SectionPayload,
      { rejectWithValue }
    ) => {
      try {
        const params =
          new URLSearchParams();

        params.append(
          "client_id",
          String(client_id)
        );

        if (user_id) {
          params.append(
            "user_id",
            String(user_id)
          );
        }

        const res =
          await axios.get(
            `${API_BASE}/programs/user?${params.toString()}`
          );

        const sectionData =
          res.data?.data || {};

        const programs =
          Array.isArray(
            sectionData?.events
          )
            ? sectionData.events
            : [];

        const getDateOnly = (
          value?: string | null
        ) => {
          if (!value) return null;

          const stringValue =
            String(value);

          if (
            stringValue.includes("T")
          ) {
            return stringValue.split(
              "T"
            )[0];
          }

          return stringValue;
        };

        return {
          can_view_events:
            Number(
              sectionData?.can_view_events ??
                1
            ),

          events_visibility:
            String(
              sectionData?.events_visibility ||
                "PRIVATE"
            ).toUpperCase(),

          is_linked_member:
            Number(
              sectionData?.is_linked_member ||
                0
            ),

          private_message:
            sectionData?.private_message ||
            null,

          total_events:
            Number(
              sectionData?.total_events ??
                programs.length
            ),

          events:
            programs.map(
              (program: any) => {
                const startDate =
                  getDateOnly(
                    program.start_date
                  );

                const endDate =
                  getDateOnly(
                    program.end_date
                  );

                return {
                  ...program,

                  event_id:
                    program.event_id ||
                    program.program_id,

                  event_title:
                    program.event_title ||
                    program.program_title ||
                    program.title ||
                    "Program",

                  from_date_time:
                    program.from_date_time ||
                    (startDate
                      ? `${startDate}T${
                          program.start_time ||
                          "00:00:00"
                        }`
                      : null),

                  to_date_time:
                    program.to_date_time ||
                    (endDate
                      ? `${endDate}T${
                          program.end_time ||
                          "23:59:59"
                        }`
                      : null),

                  location:
                    program.location ||
                    program.venue_name ||
                    program.venue ||
                    program.venue_address ||
                    program.address ||
                    null,
                };
              }
            ),
        };
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data ||
            "Failed to fetch programs"
        );
      }
    }
  );
// ==========================
// DOCUMENTS
// ==========================
export const fetchClubDocumentsSection =
  createAsyncThunk(
    "clubSection/fetchClubDocumentsSection",
    async (
      {
        client_id,
        user_id,
      }: SectionPayload,
      { rejectWithValue }
    ) => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/files/section/${client_id}${buildQuery(
              user_id
            )}`
          );

        return res.data?.data;
      } catch (error: any) {
        return rejectWithValue(
          error?.response?.data ||
            "Failed to fetch documents"
        );
      }
    }
  );

interface ClubSectionState {
  aboutData: any;
  membersData: any;
  eventsData: any;
  documentsData: any;
  mediaData: any;

  aboutLoading: boolean;
  membersLoading: boolean;
  eventsLoading: boolean;
  documentsLoading: boolean;
  mediaLoading: boolean;

  error: any;
}

const initialState: ClubSectionState = {
  aboutData: null,
  membersData: null,
  eventsData: null,
  documentsData: null,
  mediaData: null,

  aboutLoading: false,
  membersLoading: false,
  eventsLoading: false,
  documentsLoading: false,
  mediaLoading: false,

  error: null,
};

const clubSectionSlice =
  createSlice({
    name: "clubSection",

    initialState,

    reducers: {
      clearClubSections: (
        state
      ) => {
        state.aboutData =
          null;

        state.membersData =
          null;

        state.eventsData =
          null;

        state.documentsData =
          null;

        state.mediaData =
          null;

        state.error = null;
      },
    },

    extraReducers: (
      builder
    ) => {
      builder
        // ==========================
        // ABOUT
        // ==========================
        .addCase(
          fetchClubAboutSection.pending,
          (state) => {
            state.aboutLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchClubAboutSection.fulfilled,
          (
            state,
            action
          ) => {
            state.aboutLoading =
              false;

            state.aboutData =
              action.payload;
          }
        )

        .addCase(
          fetchClubAboutSection.rejected,
          (
            state,
            action
          ) => {
            state.aboutLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ==========================
        // MEMBERS
        // ==========================
        .addCase(
          fetchClubMembersSection.pending,
          (state) => {
            state.membersLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchClubMembersSection.fulfilled,
          (
            state,
            action
          ) => {
            state.membersLoading =
              false;

            state.membersData =
              action.payload;
          }
        )

        .addCase(
          fetchClubMembersSection.rejected,
          (
            state,
            action
          ) => {
            state.membersLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ==========================
        // EVENTS
        // ==========================
        .addCase(
          fetchClubEventsSection.pending,
          (state) => {
            state.eventsLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchClubEventsSection.fulfilled,
          (
            state,
            action
          ) => {
            state.eventsLoading =
              false;

            state.eventsData =
              action.payload;
          }
        )

        .addCase(
          fetchClubEventsSection.rejected,
          (
            state,
            action
          ) => {
            state.eventsLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ==========================
        // DOCUMENTS
        // ==========================
        .addCase(
          fetchClubDocumentsSection.pending,
          (state) => {
            state.documentsLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchClubDocumentsSection.fulfilled,
          (
            state,
            action
          ) => {
            state.documentsLoading =
              false;

            state.documentsData =
              action.payload;
          }
        )

        .addCase(
          fetchClubDocumentsSection.rejected,
          (
            state,
            action
          ) => {
            state.documentsLoading =
              false;

            state.error =
              action.payload;
          }
        )

        // ==========================
        // MEDIA
        // ==========================
        .addCase(
          fetchClubMediaSection.pending,
          (state) => {
            state.mediaLoading =
              true;

            state.error = null;
          }
        )

        .addCase(
          fetchClubMediaSection.fulfilled,
          (
            state,
            action
          ) => {
            state.mediaLoading =
              false;

            state.mediaData =
              action.payload;
          }
        )

        .addCase(
          fetchClubMediaSection.rejected,
          (
            state,
            action
          ) => {
            state.mediaLoading =
              false;

            state.error =
              action.payload;
          }
        );
    },
  });

export const {
  clearClubSections,
} = clubSectionSlice.actions;

export default clubSectionSlice.reducer;
